import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { questions } from '../data/quizData';

const { FiArrowRight, FiHeart, FiTarget, FiUsers, FiZap } = FiIcons;

const benefits = [
  {
    icon: FiTarget,
    title: 'See your dominant patterns',
    text: 'Discover the thought patterns that may be shaping your parenting and relationships.'
  },
  {
    icon: FiHeart,
    title: 'Understand yourself without shame',
    text: 'Get compassionate insight into the protective habits behind your reactions.'
  },
  {
    icon: FiUsers,
    title: 'Become the dad you want to be',
    text: 'Take the first practical step toward more connection, calm, and mental freedom.'
  }
];

const IntroPage = ({ participantData, setParticipantData }) => {
  const navigate = useNavigate();

  const handleStart = () => {
    setParticipantData({
      ...participantData,
      name: participantData.name || 'Dad'
    });
    navigate('/quiz');
  };

  return (
    <motion.main
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="min-h-screen flex items-center justify-center p-4 sm:p-6"
    >
      <div className="max-w-3xl w-full">
        <section className="bg-white rounded-3xl shadow-2xl overflow-hidden">
          <div className="bg-gradient-to-br from-midnight-blue via-royal-blue to-md-blue px-5 py-10 sm:px-10 sm:py-14 text-white text-center">
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.15, duration: 0.5 }}
              className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-yellow text-midnight-blue mb-5"
            >
              <SafeIcon icon={FiZap} className="text-3xl" />
            </motion.div>

            <p className="text-yellow font-bold uppercase tracking-widest text-xs sm:text-sm mb-3">
              The Dad-Mind-Fucks Assessment
            </p>
            <h1 className="text-3xl sm:text-5xl font-extrabold leading-tight mb-5">
              Discover your most dominant self-sabotaging thought patterns.
            </h1>
            <p className="text-base sm:text-xl text-white opacity-90 leading-relaxed max-w-2xl mx-auto">
              Take the first step toward mental freedom as a father—and understand
              what may be getting in the way of the dad, partner, and man you want to be.
            </p>
          </div>

          <div className="px-5 py-8 sm:px-10 sm:py-10">
            <div className="text-center mb-8">
              <h2 className="text-2xl sm:text-3xl font-bold text-midnight-blue mb-3">
                This is not about judging you.
              </h2>
              <p className="text-gray-600 leading-relaxed max-w-2xl mx-auto">
                Every dad develops mental habits to cope with pressure, responsibility,
                and uncertainty. This assessment helps you spot yours so you can respond
                with more intention instead of repeating the same patterns.
              </p>
            </div>

            <div className="grid gap-4 sm:grid-cols-3 mb-8">
              {benefits.map((benefit) => (
                <div
                  key={benefit.title}
                  className="rounded-2xl bg-gray-50 border border-gray-100 p-5"
                >
                  <SafeIcon icon={benefit.icon} className="text-2xl text-md-blue mb-3" />
                  <h3 className="font-bold text-midnight-blue mb-2">{benefit.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{benefit.text}</p>
                </div>
              ))}
            </div>

            <div className="rounded-2xl border-2 border-yellow bg-yellow bg-opacity-10 p-5 sm:p-6 mb-8">
              <div className="flex items-start gap-4">
                <SafeIcon icon={FiHeart} className="text-2xl text-yellow flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-bold text-midnight-blue mb-2">
                    Your honest answers are more valuable than perfect ones.
                  </h3>
                  <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                    There are no right or wrong answers. Answer based on what is usually
                    true for you, not the version of yourself you wish everyone could see.
                  </p>
                </div>
              </div>
            </div>

            <button
              type="button"
              onClick={handleStart}
              className="w-full bg-yellow hover:bg-opacity-90 text-midnight-blue font-bold py-4 px-6 rounded-xl shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-3 text-base sm:text-lg"
            >
              <span>Start the Assessment</span>
              <SafeIcon icon={FiArrowRight} />
            </button>

            <div className="flex items-center justify-center gap-2 mt-4 text-sm text-gray-500">
              <SafeIcon icon={FiUsers} />
              <span>{questions.length} questions • Takes about 5–7 minutes</span>
            </div>
          </div>
        </section>
      </div>
    </motion.main>
  );
};

export default IntroPage;